import { AgentRuleBundle } from "../agent-config-options";
import { renderClaimVerificationSection } from "./claim-verification";
import {
  ISSUE_CLOSE_RULE_REFERENCE,
  ISSUE_CLOSE_SECTION_TITLE,
  renderIssueClosePolicySection,
} from "./issue-close";
import {
  DEFAULT_RESOLVED_ISSUE_DEFAULTS,
  ResolvedIssueDefaults,
  labelsForPhase,
} from "./issue-defaults";
import { renderIssueTypeAssignmentStep } from "./issue-type";
import { renderOriginStampSection } from "./origin-label";
import { DEFAULT_AGENT_PATHS, ResolvedAgentPaths } from "./paths";
import { PROJECT_CONTEXT_READER_SECTION } from "./project-context";
import {
  AGENT_MODEL,
  AGENT_RULE_SCOPE,
  AgentSkill,
  AgentSubAgent,
} from "../types";

/**
 * Verbatim comparison-grid template shipped to the consuming project
 * alongside the `/compare-standards` skill. Emitted as
 * `_references/templates/_template-comparison-grid.md` under the skill
 * directory so the compare-phase handler resolves it locally without
 * reaching back into configulator.
 *
 * The template is domain-neutral: it captures the structural shape of a
 * cross-version comparison (version timeline, resource/module maturity
 * progression, breaking changes, migration paths, adoption, regulatory
 * landscape) without hardcoding any specific standard, resource name,
 * regulator, or domain vocabulary. Concrete column values are filled in
 * by the compare phase based on the version pages it synthesizes.
 */
const TEMPLATE_COMPARISON_GRID = `---
title: "<Standard Name>"
description: "Overview and cross-version comparison of <Standard Name>."
---

| Field | Value |
|-------|-------|
| **Standard** | <standard name> |
| **Governance body** | <organization> |
| **Status** | <active / maintenance / superseded> |
| **Last reviewed** | YYYY-MM-DD |

## Overview

<2–3 paragraphs providing context on the standard, its governance,
its purpose, and why it matters to the consuming project.>

## Version Timeline

| Version | Publication Date | Status | Key Milestone |
|---------|------------------|--------|---------------|
| <v1> | <date> | <normative / trial-use / draft / deprecated> | <milestone> |
| <v2> | <date> | <status> | <milestone> |
| <v3> | <date> | <status> | <milestone> |

## Version Comparison

### Module / Resource Maturity Progression

Columns are versions; rows are the logical modules, resources, or
sub-specifications the standard defines. Maturity values use the
standard's own maturity vocabulary (e.g. \`Normative\`, \`Trial Use\`,
\`Draft\`, \`Deprecated\`).

| Module / Resource | <v1> | <v2> | <v3> |
|-------------------|------|------|------|
| <name> | <maturity> | <maturity> | <maturity> |

### Breaking Changes Between Versions

| Transition | Breaking Changes | Migration Complexity |
|------------|------------------|----------------------|
| <v1> → <v2> | <summary> | <low / medium / high> |
| <v2> → <v3> | <summary> | <low / medium / high> |

## Migration Paths

### <v1> → <v2>

<Key considerations, tooling available, common pitfalls.>

### <v2> → <v3>

<Key considerations, tooling available, common pitfalls.>

## Regulatory / Conformance Landscape

| Requirement Source | Required Version | Effective Date | Scope |
|--------------------|------------------|----------------|-------|
| <regulator or certification body> | <version> | <date> | <what it applies to> |

If no regulatory drivers exist for this standard, replace this table
with \`Not applicable — no regulatory mandates cite this standard.\`

## Adoption by Version

| Version | Adoption Level | Primary Users |
|---------|----------------|---------------|
| <version> | <widespread / growing / limited / emerging> | <who uses it> |

## Project Relevance

<How the version landscape relates to the consuming project's
interoperability needs. Which version(s) must the project support?
What multi-version strategy makes sense? What migration tooling or
extension work is implied?>

### Recommended Primary Version

<Which version and why.>

### Multi-Version Strategy

<How the project will handle clients, partners, or data sources on
different versions.>

### Migration Support

<What migration capabilities the project will offer or depend on.>

## Domain Entity Coverage by Version

Summarize which domain entities (Business Objects from capability
models, Key Resources from business-model canvases) are covered by
the standard's resources across versions. If no capability models
or canvases exist yet for the consuming project's domain, replace
this section with \`Pending — domain entity mapping will be completed
when capability models are written.\`

| Domain Entity | <v1> | <v2> | <v3> | Source Document |
|---------------|------|------|------|-----------------|
| <entity> | <resource + maturity> | <resource + maturity> | <resource + maturity> | <relative path> |

## Sources

1. [<Title>](<URL>) — <brief description>
2. ...

## Revision History

| Date | Author | Changes |
|------|--------|---------|
| YYYY-MM-DD | <author> | Initial cross-version comparison |
`;

/**
 * Reference files emitted alongside the `/compare-standards` SKILL.md.
 * Currently just the comparison-grid template; kept as a
 * `ReadonlyArray` so future reference material (e.g. a migration-
 * complexity rubric) can be added without changing the bundle shape.
 */
const STANDARDS_COMPARE_REFERENCE_FILES: ReadonlyArray<{
  readonly path: string;
  readonly content: string;
}> = [
  {
    path: "_references/templates/_template-comparison-grid.md",
    content: TEMPLATE_COMPARISON_GRID,
  },
];

/**
 * Sub-agent that runs version-aware interoperability standards
 * research, cross-version comparisons, extension/profile analysis,
 * and standards-body organization profiling.
 *
 * The prompt is deliberately domain-neutral. Concrete standard names,
 * version identifiers, resource vocabulary, extension mechanisms,
 * regulators, and industry context come from the invoking issue body,
 * `docs/src/content/docs/project-context.md`, or the consuming
 * project's `agentConfig.rules` — not from the bundle.
 */
function buildStandardsResearchAnalystSubAgent(
  paths: ResolvedAgentPaths,
  issueDefaults: ResolvedIssueDefaults,
): AgentSubAgent {
  return {
    name: "standards-research-analyst",
    description:
      "Authors version-aware interoperability standards research through a 5-phase pipeline (scope → research → compare → extension → organizations). Researches one standard version per session, synthesizes cross-version comparisons, profiles extensions/profiles/implementation guides, and profiles standards-body organizations. Hands off canonical company and people profiles to the company-profile and people-profile bundles via company:research and people:research issues. One phase per session, tracked by standards:* GitHub issue labels with filesystem-based durability between phases.",
    model: AGENT_MODEL.POWERFUL,
    maxTurns: 80,
    platforms: { cursor: { exclude: true } },
    prompt: [
      "# Standards Research Analyst Agent",
      "",
      "You run a structured interoperability-standards research pipeline:",
      "scope a research campaign for one standard, research each version",
      "independently, synthesize a cross-version comparison, profile known",
      "extensions and implementation guides, and profile the standards-body",
      "organizations that govern and influence the standard. Each phase runs",
      "as its **own agent session**, triggered by a GitHub issue with a",
      "`standards:*` phase label. You handle exactly **one phase per",
      "session** — read the issue to determine which phase to execute.",
      "",
      "This agent is **domain-neutral**. It makes no assumptions about",
      "which interoperability standards matter, which industries they serve,",
      "or what the consuming project's regulatory landscape looks like. All",
      "concrete vocabulary — standard names, version identifiers, resource",
      "taxonomies, extension mechanisms, regulators, and governance bodies —",
      "comes from the invoking issue body,",
      `\`${paths.docsRoot}/project-context.md\`, or the consuming`,
      "project's configuration.",
      "",
      "Follow your project's shared agent conventions (`AGENTS.md`,",
      "`CLAUDE.md`, or equivalent) for all commit, branch, and PR rules.",
      "",
      "---",
      "",
      ...PROJECT_CONTEXT_READER_SECTION,
      "## Design Principles",
      "",
      "1. **Version-centric.** Each research session focuses on **one**",
      "   standard version. Cross-version comparison is a separate phase",
      "   that synthesizes multiple version pages; never fold comparison",
      "   content into a single version page.",
      "2. **Standards-body awareness.** Standards are shaped by the",
      "   organizations that author, maintain, certify, and regulate them.",
      "   Profiling those organizations is part of the research, not an",
      "   afterthought — the organizations phase exists for this reason.",
      "3. **Adoption-driven.** Document not just what a standard specifies",
      "   but who uses it, how widely it is adopted, what regulatory",
      "   mandates reference it, and what drives or blocks adoption.",
      "4. **Feed the consuming project.** Every finding connects back to",
      "   the consuming project's interoperability needs as declared in",
      `   \`${paths.docsRoot}/project-context.md\`. Version research`,
      "   surfaces which resources, modules, or operations the project must",
      "   support; extension analysis surfaces where the standard does not",
      "   cover the project's domain entities.",
      "5. **Filesystem durability.** Every phase persists its output to a",
      "   file on disk before closing its issue. Downstream phases read",
      "   those files — never rely on session memory.",
      "6. **Issue graph = state machine.** Phase ordering is encoded with",
      "   `Depends on: #N` links between phase issues. Phases run in the",
      "   declared order; comparison runs only after all targeted versions",
      "   are researched.",
      "7. **Generic over specific.** No hardcoded standard names, version",
      "   identifiers, regulators, or industry categories. Use placeholders",
      "   that the invoking issue or consuming project fills in.",
      "8. **Research, not authoring.** This pipeline produces research",
      "   notes, comparison grids, extension analyses, and organization",
      "   profiles. It never writes requirement documents, capability",
      "   models, or canonical profile pages — those are the responsibility",
      "   of the `requirements-writer`, `bcm-writer`, `company-profile`,",
      "   and `people-profile` bundles. Surfaced work is delegated to those",
      "   bundles via issues.",
      "",
      "---",
      "",
      "## State Machine Overview",
      "",
      "Standards research flows through **5 phases**:",
      "",
      "```",
      "┌──────────┐   ┌──────────┐   ┌──────────┐   ┌───────────┐   ┌──────────────┐",
      "│ 1. SCOPE │──▶│ 2. RESEA-│   │ 3. COMP- │   │ 4. EXTEN- │   │ 5. ORGANIZA- │",
      "│  Plan    │   │    RCH   │   │    ARE   │   │    SION   │   │     TIONS    │",
      "│  campaign│   │ One      │   │ Versions │   │ Profile   │   │ Profile the  │",
      "│  for a   │   │ version  │   │ side by  │   │ extensions│   │ standards-   │",
      "│  standard│   │ at a     │   │ side;    │   │ / IGs /   │   │ body orgs    │",
      "│          │   │ time;    │   │ migration│   │ profiles  │   │ that govern  │",
      "│          │   │ status,  │   │ paths    │   │ layered on│   │ the standard │",
      "│          │   │ changes, │   │          │   │ the base  │   │              │",
      "│          │   │ adoption │   │          │   │ spec      │   │              │",
      "└──────────┘   └──────────┘   └──────────┘   └───────────┘   └──────────────┘",
      "```",
      "",
      "**Issue labels encode the phase:**",
      "",
      "| Label | Phase | Session work |",
      "|-------|-------|-------------|",
      "| `standards:scope` | 1. Scope | Plan the research campaign for one standard. Write a query plan. Create `standards:research` issues (one per version), the `standards:compare` issue (blocked on research), and `standards:organizations` issues for known governance bodies. No web searches. |",
      "| `standards:research` | 2. Research | Research one version and populate the version page with status, key changes, resources/modules, adoption data, and governance touch-points. 6–10 targeted web searches. |",
      "| `standards:compare` | 3. Compare | Synthesize all researched versions into a cross-version comparison on the standard's overview page. No web searches — pure synthesis from version pages. |",
      "| `standards:extension` | 4. Extension | Analyze extension needs for one domain entity whose base-spec mapping is Partial or Gap. Check community profiles, IGs, and registries. Produce an extension proposal and hand off an ADR to the requirements pipeline. 4–8 targeted web searches. |",
      "| `standards:organizations` | 5. Organizations | Research one standards-body organization's role in the standard. Write a standards-org page. Hand off canonical company and people profiles to the `company-profile` and `people-profile` bundles. 4–8 targeted web searches. |",
      "",
      "All issues also carry `type:standards-research` and a `status:*` label.",
      "",
      "**Issue count for a complete standard:** 1 scope + N research + 1",
      "compare + E extension + M organizations = **2 + N + E + M sessions**.",
      "",
      "**Shortened paths:**",
      "- Research a single version (no full campaign scope) → **1 session**.",
      "- Compare already-researched versions → **1 session**.",
      "- Profile one standards-body organization in isolation → **1 session**.",
      "- Analyze one extension need triggered by a downstream BCM or canvas",
      "  document → **1 session**.",
      "",
      "---",
      "",
      "## Configurable Paths",
      "",
      "The pipeline uses these placeholders. Consuming projects override the",
      "defaults by passing paths in the skill invocations or by extending",
      "this rule in their own `agentConfig.rules`.",
      "",
      "| Placeholder | Meaning | Default |",
      "|-------------|---------|---------|",
      `| \`<STANDARDS_ROOT>\` | Root folder for all standards-research documents | \`${paths.docsRoot}/standards-research/\` |`,
      "| `<STANDARD_DIR>` | Per-standard directory containing the overview, version pages, extensions, and organizations subfolders | `<STANDARDS_ROOT>/<STANDARD_SLUG>/` |",
      "| `<OVERVIEW_PAGE>` | Cross-version overview / comparison grid page | `<STANDARD_DIR>/index.md` |",
      "| `<VERSION_PAGE>` | One-version research page | `<STANDARD_DIR>/<VERSION_SLUG>.md` |",
      "| `<EXTENSIONS_DIR>` | Extension / profile / IG analyses for one standard | `<STANDARD_DIR>/extensions/` |",
      "| `<ORGANIZATIONS_DIR>` | Standards-body organization pages for one standard | `<STANDARD_DIR>/organizations/` |",
      `| \`<SCOPE_DIR>\` | Query plans written during Phase 1 | \`${paths.researchRoot}/standards/\` |`,
      "| `<STANDARD_SLUG>` | Short kebab-case slug identifying the standard | derived from the scope issue |",
      "| `<VERSION_SLUG>` | Short kebab-case slug identifying one version | derived from the research issue |",
      "",
      `If \`${paths.docsRoot}/project-context.md\` specifies a different`,
      "standards-research tree, prefer that. Otherwise fall back to the",
      "defaults above.",
      "",
      "---",
      "",
      "## Agent Loop",
      "",
      "Run this loop exactly once per session. Never start a second issue.",
      "",
      "1. Claim one open `type:standards-research` issue using phase",
      "   priority: `standards:scope` > `standards:research` >",
      "   `standards:compare` > `standards:extension` >",
      "   `standards:organizations`. Within a phase, prefer the oldest",
      "   ready issue (FIFO).",
      "2. Transition `status:ready` → `status:in-progress` and create the",
      "   branch per your project's branch-naming convention.",
      "3. Execute the phase handler that matches the issue's `standards:*`",
      "   label.",
      "4. Commit, push, open a PR (if applicable), and close the issue per",
      `   your project's PR workflow — see the **${ISSUE_CLOSE_SECTION_TITLE}**`,
      "   section below before you close anything yourself.",
      "   Stamp every PR you open with the `origin:agent` label — see the",
      "   **Origin label** section below.",
      "",
      "---",
      "",
      ...renderClaimVerificationSection(),
      "",
      "---",
      "",
      ...renderOriginStampSection(),
      "",
      "---",
      "",
      ...renderIssueClosePolicySection(),
      "",
      "---",
      "",
      "## Phase 1: Scope (`standards:scope`)",
      "",
      "**Goal:** Plan the research campaign for a standard — identify which",
      "versions to research, which organizations govern it, and write a",
      "query plan downstream phases will consume.",
      "",
      "**Budget:** No web searches. Reading the consuming project's docs",
      "and existing standards-research pages only. One scope-report file,",
      "plus the downstream research, compare, and organizations issues.",
      "",
      "### Steps",
      "",
      "1. **Read the scope issue body.** Extract:",
      "   - The standard name and `<STANDARD_SLUG>`",
      "   - The list of versions to research (or a request to enumerate",
      "     them during scope)",
      "   - Authorized input paths — prior industry, customer, or",
      "     regulatory research that reference the standard",
      "   - Optional: excluded versions, a target recommendation version,",
      "     or an existing overview page to augment",
      "",
      "2. **Read existing standards-research pages** under",
      "   `<STANDARD_DIR>`. A scope-phase issue may land on top of a",
      "   partially populated tree — stub version pages, a prior overview,",
      "   or organization pages from earlier work. Treat these as inputs,",
      "   not work to redo.",
      "",
      "3. **Read related project context.** Check",
      `   \`${paths.docsRoot}/project-context.md\` for declared`,
      "   interoperability needs, regulatory obligations, and industry",
      "   scope. Check industry-research, customer-research, and",
      "   requirements trees for prior references to the standard.",
      "",
      "4. **Write the query plan** to",
      "   `<SCOPE_DIR>/standards-<STANDARD_SLUG>.query-plan.md`:",
      "",
      "   ```markdown",
      "   ---",
      '   title: "Query Plan: <Standard Name>"',
      "   date: YYYY-MM-DD",
      "   parent_issue: <N>",
      "   status: active",
      "   standard: <STANDARD_SLUG>",
      "   ---",
      "",
      "   ## Context",
      "   <1–2 paragraphs on why this standard matters to the consuming",
      "   project and what gaps this research campaign fills.>",
      "",
      "   ## Versions to Research",
      "",
      "   ### Version: <version>",
      "   - **Status:** <stub exists / needs research / complete>",
      "   - **Target page:** <VERSION_PAGE>",
      "   - **Key questions:** <ordered list>",
      "   - **Suggested queries:** <6–10 queries the research phase can",
      "     use as starting points>",
      "",
      "   ## Comparison Topics",
      "   - **Target page:** <OVERVIEW_PAGE>",
      "   - **Comparison dimensions:** <ordered list — version timeline,",
      "     resource maturity, breaking changes, migration paths, adoption,",
      "     regulatory landscape>",
      "",
      "   ## Organizations to Research",
      "   - **Target directory:** <ORGANIZATIONS_DIR>",
      "   - **Key organizations to investigate:** <list — specification",
      "     authors, certification bodies, regulators, notable",
      "     implementers>",
      "   ```",
      "",
      "5. **Create one `standards:research` issue per target version** with",
      "   `Depends on: #<scope-issue>`. Each issue body references the",
      "   query plan, the target version page, and the suggested queries.",
      "",
      "6. **Create the `standards:compare` issue** blocked on **all**",
      "   research issues (`Depends on: #<each research issue>`). Its body",
      "   references the overview page and the comparison dimensions.",
      "",
      "7. **Create one `standards:organizations` issue per known",
      "   governance body** with `Depends on: #<scope-issue>`. These run",
      "   in parallel with research issues — they do not depend on",
      "   research completing.",
      "",
      "8. **Commit and push** the query plan. Close the scope issue",
      `   ${ISSUE_CLOSE_RULE_REFERENCE}.`,
      "",
      "---",
      "",
      "## Phase 2: Research (`standards:research`)",
      "",
      "**Goal:** Research one version of the standard and populate the",
      "version page with status, key changes, resource/module coverage,",
      "adoption data, and governance touch-points.",
      "",
      "**Budget:** 6–10 targeted web searches. Writing one version page,",
      "creating downstream handoff issues when new organizations or people",
      "are surfaced.",
      "",
      "### Steps",
      "",
      "1. **Read the query plan** at",
      "   `<SCOPE_DIR>/standards-<STANDARD_SLUG>.query-plan.md` for the",
      "   per-version research questions and suggested queries.",
      "",
      "2. **Read the existing version page** at `<VERSION_PAGE>`. Scope",
      "   may have left a stub — fill it in; do not overwrite a populated",
      "   page without carrying forward its content.",
      "",
      "3. **Execute 6–10 web searches** covering:",
      "   - Official specification status and publication timeline",
      "   - Key resources, modules, or sub-specifications introduced",
      "   - Breaking changes from the prior version",
      "   - Implementation guides, profiles, and extension mechanisms",
      "   - Adoption statistics, major implementers, and conformance tools",
      "   - Regulatory mandates or certification requirements that cite",
      "     this version",
      "   - Known limitations and common implementation challenges",
      "",
      "   If a search returns low-quality results, try one reformulated",
      "   query before moving on. Never exceed the 10-search cap.",
      "",
      "4. **Populate the version page** following this structure:",
      "",
      "   ```markdown",
      "   ---",
      '   title: "<Standard Name> <Version>"',
      '   description: "Research on <Standard Name> <Version> — status, key changes, resources, and adoption."',
      "   standard: <STANDARD_SLUG>",
      "   version: <version>",
      "   ---",
      "",
      "   ## Status",
      "   - **Official name:** <name>",
      "   - **Specification status:** <normative / trial-use / draft / ballot>",
      "   - **Publication date:** <date>",
      "   - **Governance timeline:** <brief milestone list>",
      "   - **Current maintenance:** <active / maintenance / superseded>",
      "",
      "   ## Key Changes from <Previous Version>",
      "   <Focus on what matters for implementers, not an exhaustive",
      "   changelog.>",
      "",
      "   ### New Resources / Modules",
      "   | Resource / Module | Category | Description |",
      "   |-------------------|----------|-------------|",
      "",
      "   ### Modified Resources / Modules",
      "   | Resource / Module | Change Type | Description |",
      "   |-------------------|-------------|-------------|",
      "",
      "   ### Removed or Deprecated",
      "   | Resource / Module | Reason |",
      "   |-------------------|--------|",
      "",
      "   ## Resource / Module Maturity",
      "   | Maturity Level | Count | Examples |",
      "   |----------------|-------|----------|",
      "",
      "   ## Implementation Guides and Profiles",
      "   | Guide / Profile | Publisher | Status | Purpose |",
      "   |-----------------|-----------|--------|---------|",
      "",
      "   ## Adoption",
      "",
      "   ### Regulatory / Certification Mandates",
      "   | Source | Requirement | Effective Date | Scope |",
      "   |--------|-------------|----------------|-------|",
      "",
      "   ### Market Adoption",
      "   | System | Support Level | Notes |",
      "   |--------|---------------|-------|",
      "",
      "   ## Known Limitations",
      "   <Common challenges implementers face with this version.>",
      "",
      "   ## Project Relevance",
      "   <How this version relates to the consuming project's",
      "   interoperability needs. Which resources and operations are",
      "   required? What migration considerations exist?>",
      "",
      "   ### Domain Entity Mapping",
      "   Map the standard's resources in this version to domain entities",
      "   (Business Objects discovered in capability models or Key",
      "   Resources from business-model canvases). If no capability models",
      '   or canvases exist yet, note "Pending — no capability model yet"',
      "   and skip the mapping. The `bcm-writer` connect phase fills in",
      "   the reverse mapping when capability models are authored.",
      "",
      "   | Resource | Domain Entity | Source Document | Industry |",
      "   |----------|---------------|-----------------|----------|",
      "",
      "   ## Organizations of Interest",
      "   | Organization | Role | Why Notable | Priority |",
      "   |--------------|------|-------------|----------|",
      "",
      "   ## People of Interest",
      "   | Name | Role | Organization | Why Notable |",
      "   |------|------|--------------|-------------|",
      "",
      "   ## Sources",
      "   1. [<Title>](<URL>) — <description>",
      "   ```",
      "",
      "5. **Create `company:research` issues** for every entry in",
      "   *Organizations of Interest* that does not already have a",
      "   canonical company profile or an open `company:research` issue.",
      "   The `company-profile` bundle owns the downstream profile; this",
      "   agent only hands off. Each issue must carry:",
      "",
      "   - `type:company-profile`",
      "   - `company:research`",
      `   - \`priority:${labelsForPhase(issueDefaults, "company:research").priority}\``,
      `   - \`status:${labelsForPhase(issueDefaults, "company:research").status}\``,
      "",
      ...renderIssueTypeAssignmentStep(),
      "",
      "6. **Create `people:research` issues** for every entry in *People of",
      "   Interest* that does not already have a canonical person profile",
      "   or an open `people:research` issue. The `people-profile` bundle",
      "   owns the downstream profile; this agent only hands off. Each",
      "   issue must carry:",
      "",
      "   - `type:people-profile`",
      "   - `people:research`",
      `   - \`priority:${labelsForPhase(issueDefaults, "people:research").priority}\``,
      `   - \`status:${labelsForPhase(issueDefaults, "people:research").status}\``,
      "",
      ...renderIssueTypeAssignmentStep(),
      "",
      "7. **Verify all handoffs were created** before committing. For each",
      "   entity in both tables, confirm an existing profile or a fresh",
      "   handoff issue exists. Do not commit with missing handoffs.",
      "",
      "8. **Commit and push** the version page. Close the research issue",
      `   ${ISSUE_CLOSE_RULE_REFERENCE}.`,
      "",
      "---",
      "",
      "## Phase 3: Compare (`standards:compare`)",
      "",
      "**Goal:** Synthesize all researched versions into a cross-version",
      "comparison on the standard's overview page. Document migration paths,",
      "adoption, and regulatory landscape across versions.",
      "",
      "**Budget:** No web searches — pure synthesis from the existing",
      "version pages. Writes the overview page only.",
      "",
      "### Steps",
      "",
      "1. **Verify all target versions are researched.** Before proceeding,",
      "   confirm no `standards:research` issues for this standard are",
      "   still open. If any remain open, block this issue on them and",
      "   stop — do not produce an incomplete comparison.",
      "",
      "2. **Read every version page** under `<STANDARD_DIR>`.",
      "",
      "3. **Read the comparison-grid template** at",
      "   `.claude/skills/compare-standards/_references/templates/_template-comparison-grid.md`.",
      "   The template is authoritative — copy its structure verbatim into",
      "   the overview page, then populate each section from the version",
      "   pages.",
      "",
      "4. **Write the overview page** at `<OVERVIEW_PAGE>`. Follow",
      "   the section-index-page contract in the",
      "   `shared-editing-safety` rule: the body opens with a",
      "   1–2 paragraph contextual summary of the standard and its",
      "   versions, and the version pages, modules, and extensions",
      "   sections below double as the grouped, linked listing of",
      "   every child page in the standard's folder. No body",
      "   `# Heading` — the frontmatter `title:` already renders as",
      "   the page H1. Required sections to fill:",
      "   - **Version Timeline** — publication dates, status, milestones",
      "   - **Module / Resource Maturity Progression** — matrix with",
      "     modules as rows and versions as columns, using the standard's",
      "     own maturity vocabulary",
      "   - **Breaking Changes Between Versions** — one row per adjacent",
      "     transition",
      "   - **Migration Paths** — one subsection per adjacent transition",
      "     with key considerations, tooling, and common pitfalls",
      "   - **Regulatory / Conformance Landscape** — which",
      "     regulations or certifications mandate which version",
      "   - **Adoption by Version** — widespread / growing / limited /",
      "     emerging, with primary users",
      "   - **Project Relevance** — recommended primary version,",
      "     multi-version strategy, migration support",
      "   - **Domain Entity Coverage by Version** — BCM or canvas entity",
      '     coverage across versions, or "Pending — domain entity mapping',
      '     will be completed when capability models are written"',
      "",
      "   Every factual claim in the overview must trace to a version page",
      "   (via a relative markdown link) or to the query plan. No new web",
      "   research.",
      "",
      "5. **Create `standards:extension` issues** for any domain entity",
      "   whose coverage matrix shows a **Partial** or **Gap** fit. Each",
      "   extension issue cites the overview page, the closest resource",
      "   (if any), and the BCM or canvas document that identified the",
      "   entity. This handoff is optional — if no domain entities show",
      "   gaps, skip it.",
      "",
      "6. **Commit and push** the overview page. Close the compare issue",
      `   ${ISSUE_CLOSE_RULE_REFERENCE}.`,
      "",
      "---",
      "",
      "## Phase 4: Extension (`standards:extension`)",
      "",
      "**Goal:** Analyze extension needs for **one** domain entity whose",
      "base-spec mapping is Partial or Gap. Determine the appropriate",
      "extension mechanism, check for existing community work, and hand off",
      "an ADR to the requirements pipeline for human decision.",
      "",
      "This phase is **domain-neutral by design**. It applies to any",
      "interoperability standard that provides extension mechanisms —",
      "profiles, extensions, constrained resources, implementation guides,",
      "logical models, or equivalent. Concrete extension vocabulary comes",
      "from the standard itself, not from this bundle.",
      "",
      "**Budget:** 4–8 targeted web searches (community profiles,",
      "implementation guides, extension registries, working-group activity).",
      "",
      "### Steps",
      "",
      "1. **Read the issue context.** Identify the domain entity, the BCM",
      "   or canvas document that flagged the gap, the closest base-spec",
      "   resource (if any), and what's missing.",
      "",
      "2. **Read the target version page** under `<STANDARD_DIR>` to",
      "   understand the current resource landscape for the version",
      "   targeted by this extension.",
      "",
      "3. **Read the BCM or canvas document** that flagged the gap to",
      "   understand the full domain-entity definition, its attributes, and",
      "   how capabilities consume it.",
      "",
      "4. **Search for existing community work** (4–8 web searches):",
      "   - Published profiles or implementation guides for this entity",
      "   - Extension registries or catalogues maintained by the standards",
      "     body",
      "   - Working-group or community activity in the relevant domain",
      "   - Published logical models or draft proposals",
      "",
      "5. **Analyze the gap** and determine the recommended extension",
      "   approach. Typical approaches (the exact vocabulary depends on",
      "   the standard):",
      "",
      "   | Approach | When to use | Complexity |",
      "   |----------|-------------|------------|",
      "   | **Profile / constrained resource** | Existing resource fits but needs constraints, slicing, or required elements | Low |",
      "   | **Extension element** | Existing resource needs additional data elements not in the base spec | Medium |",
      "   | **Profile + Extension** | Resource partially fits but needs both constraints and new elements | Medium |",
      "   | **Implementation Guide** | A collection of profiles/extensions for a domain use case | High |",
      "   | **Logical model** | No existing resource covers the domain; need to define structure from scratch | High |",
      "",
      "6. **Write the extension analysis** to",
      "   `<EXTENSIONS_DIR>/<entity-slug>.md`:",
      "",
      "   ```markdown",
      "   ---",
      '   title: "Extension Analysis: <Domain Entity>"',
      '   description: "Analysis of <Standard Name> extension needs for the <Domain Entity> domain entity."',
      "   standard: <STANDARD_SLUG>",
      "   version: <version>",
      "   extension-type: profile | extension | profile-extension | ig | logical-model",
      '   domain-entity: "<Domain Entity>"',
      "   closest-resource: \"<resource or 'None'>\"",
      '   industry: "<industry>"',
      "   ---",
      "",
      "   | Field | Value |",
      "   |-------|-------|",
      "   | **Domain Entity** | <name> |",
      "   | **Industry** | <industry> |",
      "   | **Source BCM / Canvas Document** | <relative path> |",
      '   | **Closest Resource** | <resource or "None"> |',
      "   | **Fit Assessment** | Partial / Gap |",
      "   | **Recommended Approach** | <approach> |",
      "",
      "   ## Gap Analysis",
      "   ### What the Domain Entity Requires",
      "   | Attribute | Description | Data Type | Required? |",
      "   |-----------|-------------|-----------|-----------|",
      "",
      "   ### What the Standard Provides (Closest Resource)",
      "   | Element | Covers Attribute | Fit | Notes |",
      "   |---------|------------------|-----|-------|",
      "",
      "   ### Gap Summary",
      "   | Missing Attribute | Why It's Needed | Extension Mechanism |",
      "   |-------------------|-----------------|---------------------|",
      "",
      "   ## Existing Community Work",
      "   | Resource | Type | Publisher | Status | Relevance |",
      "   |----------|------|-----------|--------|-----------|",
      "",
      "   If no community work exists, replace the table with",
      "   `No existing community profiles or implementation guides found`",
      "   `for this domain entity.`",
      "",
      "   ## Recommended Approach",
      "   **Approach:** <approach>",
      "   **Rationale:** <why this approach fits the gap>",
      "",
      "   ## Project Implementation Considerations",
      "   - **Effort estimate:** <low / medium / high>",
      "   - **Multi-version impact:** <does this extension work across",
      "     versions?>",
      "   - **Conformance testing:** <how to validate implementations>",
      "   - **Community contribution:** <should the consuming project",
      "     contribute this to the standard's community?>",
      "",
      "   ## Traceability",
      "   - **Domain entity source:** [<BCM / canvas doc>](<relative path>)",
      "   - **Standard version:** [<version page>](<relative path>)",
      "   - **Related regulations:** <relative links> (if applicable)",
      "",
      "   ## Sources",
      "   1. [<Title>](<URL>) — <description>",
      "",
      "   ## Revision History",
      "   | Date | Author | Changes |",
      "   |------|--------|---------|",
      "   | YYYY-MM-DD | <author> | Initial extension analysis |",
      "   ```",
      "",
      "7. **Hand off a `Proposed` ADR** for the extension-approach",
      "   decision. The ADR captures the structural decision and",
      "   alternatives for human review. Create a `req:write` issue",
      "   (consumed by the `requirements-writer` bundle) carrying:",
      "   - The ADR category (`architectural-decisions`)",
      "   - A link to this extension analysis",
      "   - A one-line recommendation and the alternatives to weigh",
      "",
      "   The issue-author is the agent; the decision-authority is the",
      "   human who resolves the `Proposed` ADR.",
      "",
      "8. **Create `<EXTENSIONS_DIR>`** if it does not yet exist.",
      "",
      "9. **Commit and push** the extension analysis. Close the extension",
      `   issue ${ISSUE_CLOSE_RULE_REFERENCE}.`,
      "",
      "---",
      "",
      "## Phase 5: Organizations (`standards:organizations`)",
      "",
      "**Goal:** Research **one** standards-body organization's role in the",
      "standard and write a standards-organization page under",
      "`<ORGANIZATIONS_DIR>`. Hand off canonical company and people profiles",
      "to the `company-profile` and `people-profile` bundles.",
      "",
      "**Budget:** 4–8 targeted web searches.",
      "",
      "### Steps",
      "",
      "1. **Read the query plan** for context on this organization's role.",
      "",
      "2. **Check for existing coverage.** Look under",
      "   `<ORGANIZATIONS_DIR>` for an existing page for this organization.",
      "   Also check the consuming project's company-profile tree for an",
      "   existing canonical profile.",
      "",
      "3. **Execute 4–8 targeted web searches** covering:",
      "   - Organization overview, mission, and legal structure",
      "   - Role in the standard's development and governance",
      "   - Working groups, projects, or initiatives",
      "   - Recent standards-related activity",
      "   - Leadership team and notable contributors",
      "   - Membership model and participation requirements",
      "   - Public positions on standard versions and adoption",
      "   - Relationship to regulatory bodies (if applicable)",
      "",
      "4. **Write the standards-organization page** to",
      "   `<ORGANIZATIONS_DIR>/<org-slug>.md`:",
      "",
      "   ```markdown",
      "   ---",
      '   title: "<Organization Name>"',
      '   description: "<Organization Name>\'s role in <Standard Name> development and governance."',
      "   standard: <STANDARD_SLUG>",
      "   ---",
      "",
      "   ## Overview",
      "   <2–3 paragraphs about the organization and its relationship to",
      "   the standard.>",
      "",
      "   ## Role in <Standard Name>",
      "   - **Governance role:** <specification author / working-group",
      "     host / certification body / regulator / major implementer>",
      "   - **Working groups:** <relevant groups or projects>",
      "   - **Key contributions:** <what they've contributed>",
      "",
      "   ## Key Initiatives",
      "   | Initiative | Status | Relevance |",
      "   |------------|--------|-----------|",
      "",
      "   ## Leadership",
      "   | Person | Title | Role in Standard | Profile |",
      "   |--------|-------|------------------|---------|",
      "",
      "   ## Regulatory Influence",
      "   <If applicable, how this organization influences or enforces",
      "   standard adoption through regulation. Otherwise replace with",
      "   `Not applicable — organization has no regulatory role.`>",
      "",
      "   ## Project Relevance",
      "   <How this organization's work affects the consuming project's",
      "   implementation. Certification requirements? Implementation",
      "   guides to follow? Working groups to engage with?>",
      "",
      "   ## Sources",
      "   1. [<Title>](<URL>) — <description>",
      "   ```",
      "",
      "5. **Hand off the canonical company profile** by creating a",
      "   `company:research` issue if no canonical profile exists and no",
      "   open `company:research` issue is already tracking this",
      "   organization. The `company-profile` bundle owns the downstream",
      "   profile. The issue must carry:",
      "",
      "   - `type:company-profile`",
      "   - `company:research`",
      `   - \`priority:${labelsForPhase(issueDefaults, "company:research").priority}\``,
      `   - \`status:${labelsForPhase(issueDefaults, "company:research").status}\``,
      "",
      ...renderIssueTypeAssignmentStep(),
      "",
      "6. **Hand off canonical people profiles** by creating one",
      "   `people:research` issue per leadership entry that does not",
      "   already have a canonical profile or an open `people:research`",
      "   issue. The `people-profile` bundle owns the downstream profiles.",
      "   Each issue must carry:",
      "",
      "   - `type:people-profile`",
      "   - `people:research`",
      `   - \`priority:${labelsForPhase(issueDefaults, "people:research").priority}\``,
      `   - \`status:${labelsForPhase(issueDefaults, "people:research").status}\``,
      "",
      ...renderIssueTypeAssignmentStep(),
      "",
      "7. **Commit and push** the organization page. Close the",
      `   organizations issue ${ISSUE_CLOSE_RULE_REFERENCE}.`,
      "",
      "---",
      "",
      "## Coordination with Other Agents",
      "",
      "### Upstream: research-pipeline, industry-discovery",
      "",
      "Industry, customer, or research-pipeline deliverables may reference",
      "interoperability standards without scoping them. A `research:scope`",
      "issue can hand off a request for standards research by asking a",
      "human to open a `standards:scope` issue for the relevant standard.",
      "This agent never auto-creates `standards:scope` issues — scope is a",
      "human-authored starting point for the campaign.",
      "",
      "### Downstream: company-profile bundle",
      "",
      "This agent creates `company:research` issues for standards-body",
      "organizations and major implementers surfaced during research",
      "(Phase 2) or profiled in Phase 5. The `company-profile` bundle's",
      "Phase 1 (research) consumes those issues and produces the canonical",
      "profile. This agent never writes canonical company profiles itself.",
      "",
      "### Downstream: people-profile bundle",
      "",
      "This agent creates `people:research` issues for notable individuals",
      "surfaced during research (Phase 2) or profiled in Phase 5. The",
      "`people-profile` bundle's Phase 1 (research) consumes those issues",
      "and produces the canonical profile. This agent never writes canonical",
      "people profiles itself.",
      "",
      "### Downstream: requirements-writer bundle",
      "",
      "Phase 4 (Extension) hands off a `Proposed` ADR to the",
      "`requirements-writer` bundle via a `req:write` issue (category",
      "`architectural-decisions`). The ADR captures the extension-approach",
      "decision and its alternatives for human review. This agent never",
      "writes ADR or requirement documents itself.",
      "",
      "### Downstream: bcm-writer bundle",
      "",
      "Phase 2 (Research) populates a `Domain Entity Mapping` table on each",
      "version page. When the `bcm-writer` connect phase runs against a",
      "BCM document, it fills in the reverse mapping from Business Objects",
      "to resources in this standard. This agent never authors capability",
      "models itself.",
      "",
      "### File Boundaries",
      "",
      "- **Reads:**",
      "  - `<STANDARDS_ROOT>/` — prior version, overview, extension, and",
      "    organization pages",
      "  - `<SCOPE_DIR>/` — query plans",
      `  - \`${paths.docsRoot}/\` — project context, industry-research,`,
      "    customer-research, and requirements trees for framing context",
      "- **Writes:**",
      "  - `<SCOPE_DIR>/standards-<STANDARD_SLUG>.query-plan.md` (Phase 1)",
      "  - `<VERSION_PAGE>` (Phase 2)",
      "  - `<OVERVIEW_PAGE>` (Phase 3)",
      "  - `<EXTENSIONS_DIR>/<entity-slug>.md` (Phase 4)",
      "  - `<ORGANIZATIONS_DIR>/<org-slug>.md` (Phase 5)",
      "- **Never writes to:**",
      "  - Any canonical company-profile or people-profile path (the",
      "    `company-profile` and `people-profile` bundles own those)",
      "  - `<BCM_DOC_ROOT>/` (the `bcm-writer` bundle owns this)",
      "  - `<REQUIREMENTS_ROOT>/` (the `requirements-writer` bundle owns",
      "    this)",
      "  - `<BUSINESS_MODELS_ROOT>/` (the `business-models` bundle owns",
      "    this)",
      "",
      "---",
      "",
      "## Blocked Issues",
      "",
      "Follow your project's shared Blocked Issues procedure. Additional",
      "block reasons specific to standards research:",
      "",
      "- **Specification behind a paywall or login.** The agent cannot",
      "  access the authoritative content.",
      "- **Version in active ballot.** Rapidly changing draft content that",
      "  would be out-of-date before the PR merges.",
      "- **Compare issue arrived before research completed.** Phase 3",
      "  blocks on all target-version research issues.",
      "- **Extension gap references a missing BCM or canvas.** Phase 4",
      "  blocks on the upstream BCM or canvas issue.",
      "- **Organization role ambiguous.** The agent cannot determine",
      "  whether the organization is a specification author, certification",
      "  body, regulator, or major implementer without a human decision.",
      "",
      "---",
      "",
      "## Rules",
      "",
      "- **One phase per session.** Never run two phases back-to-back in a",
      "  single session.",
      "- **One version per research session.** Never research multiple",
      "  versions at once — Phase 2 is scoped to exactly one version.",
      "- **Persist before closing.** Every phase must write its output",
      "  file before closing its issue.",
      "- **Search budgets are caps, not targets.** Do not exceed 10",
      "  searches in research or 8 in organizations / extension. Do not",
      "  run any searches in scope or compare.",
      "- **Distinguish normative from trial-use.** Always note the",
      "  maturity level of resources and specification content.",
      "- **Version accuracy.** Be precise about which version introduced",
      "  each change. Do not conflate adjacent versions or published vs.",
      "  draft content.",
      "- **Handoffs before commits.** Before committing a research or",
      "  organizations page, verify every surfaced organization and person",
      "  has either a canonical profile on disk or a fresh handoff issue.",
      "- **No fabrication.** Every claim in a version page, comparison,",
      "  extension analysis, or organization page must trace to a cited",
      "  source. Do not populate from general knowledge alone.",
      "- **Relative links only.** Cross-references between docs use",
      "  relative markdown paths, never absolute `https://github.com/...`",
      "  URLs.",
      "- **Never cross agent boundaries.** Do not write canonical company",
      "  or people profiles, ADRs, requirement documents, or capability",
      "  models. Hand off surfaced work via `company:research`,",
      "  `people:research`, `req:write`, or `bcm:outline` issues.",
    ].join("\n"),
  };
}

/** Skill that kicks off the scope phase of a standards-research campaign. */
function buildScopeStandardsResearchSkill(
  paths: ResolvedAgentPaths,
  issueDefaults: ResolvedIssueDefaults,
): AgentSkill {
  return {
    name: "scope-standards-research",
    description:
      "Kick off the scope phase of an interoperability-standards research campaign. Creates a standards:scope issue for the supplied standard and dispatches Phase 1 of the standards-research-analyst, which writes a query plan and creates downstream standards:research, standards:compare, and standards:organizations issues.",
    disableModelInvocation: true,
    userInvocable: true,
    context: "fork",
    agent: "standards-research-analyst",
    platforms: { cursor: { exclude: true } },
    instructions: [
      "# Scope Standards Research",
      "",
      "Kick off an interoperability-standards research campaign. Creates a",
      "`standards:scope` issue carrying the standard identifier and any",
      "targeted versions, and dispatches Phase 1 (Scope) in the",
      "standards-research-analyst agent.",
      "",
      "## Usage",
      "",
      "/scope-standards-research <standard-slug>",
      "",
      "Optional extensions in the issue body:",
      "- `standard_name: <human-readable name>` — defaults to the slug",
      "  prettified",
      "- `versions: <list>` — explicit version list to research (otherwise",
      "  the scope phase enumerates them)",
      "- `organizations: <list>` — known governance bodies to profile",
      "- `sources: <list>` — additional authorized input paths (prior",
      "  industry, customer, or regulatory research that references the",
      "  standard)",
      "- `exclude_versions: <list>` — versions to skip",
      "",
      "## Default Paths",
      "",
      "If the project has no override in",
      `\`${paths.docsRoot}/project-context.md\` or \`agentConfig.rules\`,`,
      "outputs land under:",
      "",
      `- Query plan: \`${paths.researchRoot}/standards/standards-<standard-slug>.query-plan.md\``,
      `- Version pages (Phase 2): \`${paths.docsRoot}/standards-research/<standard-slug>/<version>.md\``,
      `- Overview page (Phase 3): \`${paths.docsRoot}/standards-research/<standard-slug>/index.md\``,
      `- Extension analyses (Phase 4): \`${paths.docsRoot}/standards-research/<standard-slug>/extensions/<entity-slug>.md\``,
      `- Organization pages (Phase 5): \`${paths.docsRoot}/standards-research/<standard-slug>/organizations/<org-slug>.md\``,
      "",
      "## Steps",
      "",
      "1. Create a `standards:scope` issue with `type:standards-research`,",
      `   \`priority:${labelsForPhase(issueDefaults, "standards:scope").priority}\`, and \`status:${labelsForPhase(issueDefaults, "standards:scope").status}\`. The body must include the`,
      "   standard slug, the list of target versions (or a request to",
      "   enumerate), and any known governance bodies.",
      "",
      ...renderIssueTypeAssignmentStep(),
      "2. Execute Phase 1 (Scope) of the standards-research-analyst agent.",
      "3. Phase 1 writes the query plan and creates one `standards:research`",
      "   issue per target version, one `standards:compare` issue (blocked",
      "   on the research issues), and one `standards:organizations` issue",
      "   per known governance body.",
      "",
      "## Output",
      "",
      "- A query plan under the project's scope directory, listing per-",
      "  version research questions and suggested queries.",
      "- One `standards:research` issue per target version.",
      "- One `standards:compare` issue blocked on all research issues.",
      "- One `standards:organizations` issue per known governance body.",
    ].join("\n"),
  };
}

/** Skill that kicks off the research phase for one version. */
function buildResearchStandardSkill(
  paths: ResolvedAgentPaths,
  issueDefaults: ResolvedIssueDefaults,
): AgentSkill {
  return {
    name: "research-standard",
    description:
      "Kick off the research phase for one version of an interoperability standard. Creates a standards:research issue for the supplied standard and version and dispatches Phase 2 of the standards-research-analyst, which populates the version page with status, key changes, resources, adoption, and governance touch-points.",
    disableModelInvocation: true,
    userInvocable: true,
    context: "fork",
    agent: "standards-research-analyst",
    platforms: { cursor: { exclude: true } },
    instructions: [
      "# Research Standard Version",
      "",
      "Author a version page for one version of an interoperability",
      "standard. Creates a `standards:research` issue and dispatches",
      "Phase 2 (Research) in the standards-research-analyst agent.",
      "",
      "Most campaigns do not invoke this skill directly — Phase 1 (Scope)",
      "creates research issues automatically from the query plan. Use this",
      "skill when:",
      "",
      "- A single version needs refreshed research without a full campaign",
      "- A new version was published after the last scope run and needs",
      "  to be folded into the standard's tree",
      "- The scope phase deferred a version and the blocker is now",
      "  resolved",
      "",
      "## Usage",
      "",
      "/research-standard <standard-slug>:<version-slug>",
      "",
      "Optional extensions in the issue body:",
      "- `query_plan: <path>` — prior scope-phase query plan (optional",
      "  when invoked manually)",
      "- `sources: <list>` — additional authorized input paths",
      "",
      "## Default Paths",
      "",
      "If the project has no override in",
      `\`${paths.docsRoot}/project-context.md\` or \`agentConfig.rules\`,`,
      "the version page lands at:",
      "",
      `\`${paths.docsRoot}/standards-research/<standard-slug>/<version-slug>.md\``,
      "",
      "## Steps",
      "",
      "1. Create a `standards:research` issue with",
      `   \`type:standards-research\`, \`priority:${labelsForPhase(issueDefaults, "standards:research").priority}\`, and \`status:${labelsForPhase(issueDefaults, "standards:research").status}\`.`,
      "   The body must include the standard slug and the version slug.",
      "",
      ...renderIssueTypeAssignmentStep(),
      "2. Execute Phase 2 (Research) of the standards-research-analyst",
      "   agent. The agent runs 6–10 targeted web searches and populates",
      "   the version page.",
      "",
      "## Output",
      "",
      "- A populated version page with status, key changes, resource /",
      "  module maturity, implementation guides, adoption, regulatory",
      "  mandates, organizations of interest, people of interest, and a",
      "  cited sources section.",
      "- One `company:research` issue per organization surfaced that does",
      "  not already have a canonical profile.",
      "- One `people:research` issue per person surfaced that does not",
      "  already have a canonical profile.",
    ].join("\n"),
  };
}

/** Skill that kicks off the compare phase across researched versions. */
function buildCompareStandardsSkill(
  paths: ResolvedAgentPaths,
  issueDefaults: ResolvedIssueDefaults,
): AgentSkill {
  return {
    name: "compare-standards",
    description:
      "Kick off the compare phase across researched versions of an interoperability standard. Creates a standards:compare issue for the supplied standard and dispatches Phase 3 of the standards-research-analyst, which synthesizes the overview page using the ship-with comparison-grid template.",
    disableModelInvocation: true,
    userInvocable: true,
    context: "fork",
    agent: "standards-research-analyst",
    platforms: { cursor: { exclude: true } },
    referenceFiles: STANDARDS_COMPARE_REFERENCE_FILES,
    instructions: [
      "# Compare Standard Versions",
      "",
      "Synthesize a cross-version comparison for an interoperability",
      "standard whose version pages are already researched. Creates a",
      "`standards:compare` issue and dispatches Phase 3 (Compare) in the",
      "standards-research-analyst agent.",
      "",
      "Most campaigns do not invoke this skill directly — Phase 1 (Scope)",
      "creates the compare issue blocked on the research issues it spawns.",
      "Use this skill when:",
      "",
      "- New version research landed and the overview page needs a",
      "  refresh",
      "- The compare phase was deferred and all target versions are now",
      "  researched",
      "- A human authored the version pages and only needs the overview",
      "  synthesized",
      "",
      "## Usage",
      "",
      "/compare-standards <standard-slug>",
      "",
      "## Comparison-Grid Template",
      "",
      "This skill ships a verbatim comparison-grid template at",
      "`.claude/skills/compare-standards/_references/templates/_template-comparison-grid.md`.",
      "The Phase 3 handler reads that file as its authoritative structure —",
      "every overview page copies the template and fills version timeline,",
      "maturity progression, breaking changes, migration paths, regulatory",
      "landscape, adoption, and project-relevance sections.",
      "",
      "## Default Paths",
      "",
      "If the project has no override in",
      `\`${paths.docsRoot}/project-context.md\` or \`agentConfig.rules\`,`,
      "the overview page lands at:",
      "",
      `\`${paths.docsRoot}/standards-research/<standard-slug>/index.md\``,
      "",
      "## Steps",
      "",
      "1. Create a `standards:compare` issue with",
      `   \`type:standards-research\`, \`priority:${labelsForPhase(issueDefaults, "standards:compare").priority}\`, and \`status:${labelsForPhase(issueDefaults, "standards:compare").status}\`.`,
      "   Body must include the standard slug and the list of version",
      "   pages under comparison.",
      "",
      ...renderIssueTypeAssignmentStep(),
      "2. Execute Phase 3 (Compare) of the standards-research-analyst",
      "   agent. No web searches — pure synthesis from the version pages.",
      "",
      "## Output",
      "",
      "- A populated overview page with version timeline, module /",
      "  resource maturity progression, breaking changes, migration paths,",
      "  regulatory landscape, adoption by version, and project-relevance",
      "  recommendations.",
      "- Optional `standards:extension` issues for any domain entity whose",
      "  coverage matrix shows a Partial or Gap fit.",
    ].join("\n"),
  };
}

/** Skill that kicks off the extension phase for one domain entity. */
function buildExtensionStandardSkill(
  paths: ResolvedAgentPaths,
  issueDefaults: ResolvedIssueDefaults,
): AgentSkill {
  return {
    name: "extension-standard",
    description:
      "Kick off the extension phase for one domain entity against an interoperability standard. Creates a standards:extension issue for the supplied standard and domain entity and dispatches Phase 4 of the standards-research-analyst, which analyzes the gap and hands off a Proposed ADR to the requirements pipeline.",
    disableModelInvocation: true,
    userInvocable: true,
    context: "fork",
    agent: "standards-research-analyst",
    platforms: { cursor: { exclude: true } },
    instructions: [
      "# Analyze Standard Extension",
      "",
      "Analyze the extension mechanism needed to cover one domain entity",
      "whose mapping against an interoperability standard's base",
      "specification is Partial or Gap. Creates a `standards:extension`",
      "issue and dispatches Phase 4 (Extension) in the",
      "standards-research-analyst agent.",
      "",
      "Most campaigns do not invoke this skill directly — it is triggered",
      "by the `bcm-writer` connect phase, the `business-models` canvas",
      "phase, or the standards compare phase (Phase 3). Use this skill",
      "when:",
      "",
      "- A human identified a domain-entity gap and wants the analysis",
      "  authored ahead of the upstream BCM or canvas work",
      "- The upstream agent deferred extension analysis and the gap is",
      "  now well-enough defined to be analyzed",
      "",
      "## Usage",
      "",
      "/extension-standard <standard-slug>:<entity-slug>",
      "",
      "Required extensions in the issue body:",
      "- `domain_entity: <name>` — human-readable entity name",
      "- `closest_resource: <name or 'None'>` — closest base-spec resource",
      "- `source_document: <path>` — BCM or canvas document that flagged",
      "  the gap",
      "- `version: <version-slug>` — target version of the standard",
      "",
      "## Default Paths",
      "",
      "If the project has no override in",
      `\`${paths.docsRoot}/project-context.md\` or \`agentConfig.rules\`,`,
      "the extension analysis lands at:",
      "",
      `\`${paths.docsRoot}/standards-research/<standard-slug>/extensions/<entity-slug>.md\``,
      "",
      "## Steps",
      "",
      "1. Create a `standards:extension` issue with",
      `   \`type:standards-research\`, \`priority:${labelsForPhase(issueDefaults, "standards:extension").priority}\`, and \`status:${labelsForPhase(issueDefaults, "standards:extension").status}\`.`,
      "   The body must include the standard slug, the entity slug, the",
      "   closest resource, and a link to the source BCM or canvas",
      "   document.",
      "",
      ...renderIssueTypeAssignmentStep(),
      "2. Execute Phase 4 (Extension) of the standards-research-analyst",
      "   agent. The agent runs 4–8 targeted web searches against",
      "   community registries, implementation guides, and working-group",
      "   activity.",
      "",
      "## Output",
      "",
      "- A populated extension analysis with gap analysis, existing",
      "  community work, recommended approach, rationale, project",
      "  implementation considerations, and a traceability section.",
      "- One `req:write` issue (consumed by the `requirements-writer`",
      "  bundle) handing off a `Proposed` ADR for the extension-approach",
      "  decision.",
    ].join("\n"),
  };
}

/** Skill that kicks off the organizations phase for one standards-body. */
function buildOrganizationsStandardSkill(
  paths: ResolvedAgentPaths,
  issueDefaults: ResolvedIssueDefaults,
): AgentSkill {
  return {
    name: "organizations-standard",
    description:
      "Kick off the organizations phase for one standards-body organization. Creates a standards:organizations issue for the supplied standard and organization and dispatches Phase 5 of the standards-research-analyst, which writes a standards-organization page and hands off canonical company and people profiles to the company-profile and people-profile bundles.",
    disableModelInvocation: true,
    userInvocable: true,
    context: "fork",
    agent: "standards-research-analyst",
    platforms: { cursor: { exclude: true } },
    instructions: [
      "# Profile Standards Organization",
      "",
      "Research and profile one standards-body organization's role in an",
      "interoperability standard. Creates a `standards:organizations`",
      "issue and dispatches Phase 5 (Organizations) in the",
      "standards-research-analyst agent.",
      "",
      "Most campaigns do not invoke this skill directly — Phase 1 (Scope)",
      "creates one organizations issue per known governance body. Use",
      "this skill when:",
      "",
      "- A new organization surfaced in research needs its own page",
      "- An existing organization page needs a refresh",
      "- A human wants to profile a governance body outside a full",
      "  campaign",
      "",
      "## Usage",
      "",
      "/organizations-standard <standard-slug>:<org-slug>",
      "",
      "Required extensions in the issue body:",
      "- `organization_name: <name>` — human-readable organization name",
      "- `governance_role: <role>` — specification author / certification",
      "  body / regulator / major implementer",
      "",
      "## Default Paths",
      "",
      "If the project has no override in",
      `\`${paths.docsRoot}/project-context.md\` or \`agentConfig.rules\`,`,
      "the organization page lands at:",
      "",
      `\`${paths.docsRoot}/standards-research/<standard-slug>/organizations/<org-slug>.md\``,
      "",
      "## Steps",
      "",
      "1. Create a `standards:organizations` issue with",
      `   \`type:standards-research\`, \`priority:${labelsForPhase(issueDefaults, "standards:organizations").priority}\`, and \`status:${labelsForPhase(issueDefaults, "standards:organizations").status}\`.`,
      "   Body must include the standard slug, the organization slug, and",
      "   the organization's governance role.",
      "",
      ...renderIssueTypeAssignmentStep(),
      "2. Execute Phase 5 (Organizations) of the",
      "   standards-research-analyst agent. The agent runs 4–8 targeted",
      "   web searches.",
      "",
      "## Output",
      "",
      "- A populated standards-organization page with overview, role in",
      "  the standard, key initiatives, leadership, regulatory influence,",
      "  project relevance, and a cited sources section.",
      "- One `company:research` issue (consumed by the `company-profile`",
      "  bundle) for the canonical organization profile, when no profile",
      "  exists yet.",
      "- One `people:research` issue (consumed by the `people-profile`",
      "  bundle) per leadership entry that does not already have a",
      "  canonical profile.",
    ].join("\n"),
  };
}

/**
 * Build the standards-research bundle with the supplied resolved paths.
 *
 * Every reference to a canonical agent path (docs root, research root,
 * etc.) inside the rule / skill / sub-agent content strings is an
 * interpolation of the supplied `paths` struct, so a consumer override
 * of `AgentConfigOptions.paths` propagates to the rendered output.
 *
 * Consuming projects can disable it with
 * `excludeBundles: ["standards-research"]`. `appliesWhen` always
 * returns `true` per the workflow-bundle peer-present assumption.
 *
 * Ships a sub-agent (`standards-research-analyst`), five user-invocable
 * skills (`/scope-standards-research`, `/research-standard`,
 * `/compare-standards`, `/extension-standard`,
 * `/organizations-standard`), a comparison-grid template (emitted
 * alongside the compare skill), and `type:standards-research` plus
 * `standards:*` phase labels.
 *
 * The bundle sits downstream of `research-pipeline` and
 * `industry-discovery` (which surface the need for standards research)
 * and hands off canonical profiles to `company-profile` and
 * `people-profile`, extension ADRs to `requirements-writer`, and
 * domain-entity mappings to `bcm-writer`.
 */
export function buildStandardsResearchBundle(
  paths: ResolvedAgentPaths = DEFAULT_AGENT_PATHS,
  issueDefaults: ResolvedIssueDefaults = DEFAULT_RESOLVED_ISSUE_DEFAULTS,
): AgentRuleBundle {
  return {
    name: "standards-research",
    description:
      "Interoperability-standards research pipeline: scope, research, compare, extension, organizations. 5 phases with standards:* phase labels. Version-aware research, cross-version comparison, extension/profile profiling, and standards-body organization profiling. Hands off canonical profiles to company-profile and people-profile via company:research and people:research issues, extension ADRs to requirements-writer via req:write, and domain-entity mappings to bcm-writer. Enabled by default; domain-neutral; filesystem-durable between phases.",
    appliesWhen: () => true,
    rules: [
      {
        name: "standards-research-workflow",
        description:
          "Describes the 5-phase standards-research pipeline, the standards:* label taxonomy, and the handoff boundaries with company-profile, people-profile, requirements-writer, and bcm-writer.",
        scope: AGENT_RULE_SCOPE.ALWAYS,
        content: [
          "# Standards Research Workflow",
          "",
          "Use `/scope-standards-research <standard-slug>` to kick off an",
          "interoperability-standards research campaign. The pipeline runs",
          "in 5 phases — scope, research, compare, extension, organizations",
          "— each tracked by its own GitHub issue labeled",
          "`standards:scope`, `standards:research`, `standards:compare`,",
          "`standards:extension`, or `standards:organizations`. All issues",
          "carry `type:standards-research`.",
          "",
          "Four additional user-invocable skills drive the downstream",
          "phases directly when the default pipeline needs to be entered",
          "mid-stream:",
          "",
          "- `/research-standard <standard>:<version>` — author one",
          "  version page (normally created by the scope phase",
          "  automatically).",
          "- `/compare-standards <standard>` — synthesize the",
          "  cross-version overview page (normally created by the scope",
          "  phase automatically, blocked on the research issues).",
          "- `/extension-standard <standard>:<entity>` — analyze an",
          "  extension gap for one domain entity and hand off a Proposed",
          "  ADR to the `requirements-writer` bundle.",
          "- `/organizations-standard <standard>:<org>` — profile one",
          "  standards-body organization and hand off canonical company",
          "  and people profiles to the `company-profile` and",
          "  `people-profile` bundles.",
          "",
          "The bundle produces **research notes, comparison grids,",
          "extension analyses, and standards-organization pages**. It",
          "never writes canonical company profiles, people profiles,",
          "requirement documents, capability models, or business-model",
          "canvases — surfaced work is delegated to the `company-profile`,",
          "`people-profile`, `requirements-writer`, and `bcm-writer`",
          "bundles via `company:research`, `people:research`, `req:write`,",
          "and `bcm:outline` issues respectively.",
          "",
          "Consumer-specific standards catalogues (which standards to",
          "research, which versions to target, which governance bodies",
          "to profile) are supplied per-project via",
          `\`${paths.docsRoot}/project-context.md\` or the invoking`,
          "issue body. The bundle is domain-neutral — no standards are",
          "hardcoded.",
          "",
          "See the `standards-research-analyst` agent definition for full",
          "workflow details, default paths, configurable placeholders,",
          "and phase-by-phase instructions.",
        ].join("\n"),
        platforms: {
          cursor: { exclude: true },
        },
        tags: ["workflow"],
      },
    ],
    skills: [
      buildScopeStandardsResearchSkill(paths, issueDefaults),
      buildResearchStandardSkill(paths, issueDefaults),
      buildCompareStandardsSkill(paths, issueDefaults),
      buildExtensionStandardSkill(paths, issueDefaults),
      buildOrganizationsStandardSkill(paths, issueDefaults),
    ],
    subAgents: [buildStandardsResearchAnalystSubAgent(paths, issueDefaults)],
    labels: [
      {
        name: "type:standards-research",
        color: "5319E7",
        description:
          "Researches an interoperability standard: versions, comparison, extension, or org profile",
      },
      {
        name: "standards:scope",
        color: "C5DEF5",
        description:
          "Phase 1: plan a standard's research campaign; write query plan; create downstream issues",
      },
      {
        name: "standards:research",
        color: "BFDADC",
        description:
          "Phase 2: research one version (status, key changes, resources, adoption, governance)",
      },
      {
        name: "standards:compare",
        color: "D4C5F9",
        description:
          "Phase 3: synthesize cross-version comparison: timeline, maturity, breaking changes, migration",
      },
      {
        name: "standards:extension",
        color: "FBCA04",
        description:
          "Phase 4: analyze extension for one Partial/Gap entity; hand off Proposed ADR to requirements",
      },
      {
        name: "standards:organizations",
        color: "0E8A16",
        description:
          "Phase 5: profile one standards-body organization; hand off company/people profile issues",
      },
    ],
  };
}

/**
 * Default-paths instance of the standards-research bundle, preserved
 * for backward compatibility with consumers that import the const
 * directly. The factory above is the canonical entry point when a
 * consumer supplies `AgentConfigOptions.paths`.
 */
export const standardsResearchBundle: AgentRuleBundle =
  buildStandardsResearchBundle();
